import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './PrivateLessons.css';
import {
  API_BASE,
  authHeaders,
  buildCalendarDays,
  formatTime,
  getMonthLabel,
  toDateKey
} from './privateLessonUtils.js';

function AdminPrivateLessonBooking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [students, setStudents] = useState([]);
  const [links, setLinks] = useState([]);
  const [durations, setDurations] = useState([60, 90, 120]);

  const [values, setValues] = useState({
    student_id: searchParams.get('student_id') || '',
    link_id: '',
    duration_minutes: Number(searchParams.get('duration') || 60),
    notes: ''
  });

  const [studentSearch, setStudentSearch] = useState('');
  const [slots, setSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [savingStartAt, setSavingStartAt] = useState('');
  const [bookedItems, setBookedItems] = useState([]);

  const formatDateOnly = (value) => {
    if (!value) return '-';

    try {
      const date =
        String(value).length === 10
          ? new Date(`${value}T12:00:00`)
          : new Date(value);

      return new Intl.DateTimeFormat('nl-NL', {
        dateStyle: 'medium'
      }).format(date);
    } catch {
      return value;
    }
  };

  const formatSlotTime = (slot, field) => {
    if (!slot) return '-';

    const timeValue = field === 'start' ? slot.start_time : slot.end_time;
    const dateValue = field === 'start' ? slot.start_at : slot.end_at;

    if (timeValue && /^\d{2}:\d{2}/.test(String(timeValue))) {
      return String(timeValue).slice(0, 5);
    }

    return formatTime(dateValue);
  };

  const getSlotDateKey = (slot) => {
    if (slot?.slot_date) return String(slot.slot_date).slice(0, 10);
    return toDateKey(slot?.start_at);
  };

  const getSlotLabel = (slot) => {
    return `${formatSlotTime(slot, 'start')} - ${formatSlotTime(slot, 'end')}`;
  };

  const getStudentLabel = (student) => {
    const parts = [
      student.student_name,
      student.location_name ? `(${student.location_name})` : ''
    ];

    return parts.filter(Boolean).join(' ') || student.student_id;
  };

  const fetchOptions = async () => {
    try {
      setIsLoadingOptions(true);

      const res = await axios.get(`${API_BASE}/private-lesson/booking/options`, {
        headers: authHeaders()
      });

      const data = res.data?.data || {};
      const nextStudents = Array.isArray(data.students) ? data.students : [];
      const nextLinks = Array.isArray(data.links) ? data.links : [];
      const nextDurations =
        Array.isArray(data.durations) && data.durations.length > 0
          ? data.durations
          : [60, 90, 120];

      setStudents(nextStudents);
      setLinks(nextLinks);
      setDurations(nextDurations);

      setValues((prev) => {
        const next = { ...prev };

        if (!next.student_id && nextStudents.length === 1) {
          next.student_id = nextStudents[0].student_id;
        }

        if (!next.duration_minutes || !nextDurations.includes(Number(next.duration_minutes))) {
          next.duration_minutes = nextDurations[0];
        }

        return next;
      });
    } catch (err) {
      console.error('Error fetching admin private lesson booking options', err);
      setStudents([]);
      setLinks([]);
      alert(err?.response?.data?.error || 'Boekingsopties ophalen mislukt.');
    } finally {
      setIsLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;

    return students.filter((student) => {
      const haystack = [
        student.student_name,
        student.location_name,
        student.firstname,
        student.lastname
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [students, studentSearch]);

  const availableLinks = useMemo(() => {
    if (!values.student_id) return [];

    return links.filter((link) => link.student_id === values.student_id);
  }, [links, values.student_id]);

  useEffect(() => {
    if (availableLinks.length === 1 && !values.link_id) {
      setValues((prev) => ({ ...prev, link_id: availableLinks[0].link_id }));
    }

    if (values.link_id && !availableLinks.some((link) => link.link_id === values.link_id)) {
      setValues((prev) => ({ ...prev, link_id: '' }));
    }
  }, [availableLinks, values.link_id]);

  const selectedLink = useMemo(
    () => availableLinks.find((link) => link.link_id === values.link_id) || null,
    [availableLinks, values.link_id]
  );

  const selectedStudent = useMemo(
    () => students.find((student) => student.student_id === values.student_id) || null,
    [students, values.student_id]
  );

  const fetchSlots = async () => {
    if (!values.student_id || !selectedLink?.docent_id) {
      setSlots([]);
      setSelectedDateKey('');
      return;
    }

    try {
      setIsLoadingSlots(true);
      const from = toDateKey(currentMonth) || toDateKey(new Date());

      const res = await axios.get(`${API_BASE}/private-lesson/booking/slots`, {
        params: {
          student_id: values.student_id,
          docent_id: selectedLink.docent_id,
          subject: selectedLink.subject || undefined,
          location_id: selectedLink.location_id || selectedStudent?.location_id || undefined,
          duration_minutes: values.duration_minutes,
          from,
          days: 42
        },
        headers: authHeaders()
      });

      const nextSlots = Array.isArray(res.data?.data?.slots) ? res.data.data.slots : [];
      setSlots(nextSlots);
      setSelectedDateKey('');
    } catch (err) {
      console.error('Error fetching admin private lesson slots', err);
      setSlots([]);
      setSelectedDateKey('');
      alert(err?.response?.data?.error || 'Beschikbare tijden ophalen mislukt.');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.student_id, values.link_id, values.duration_minutes, currentMonth]);

  const slotsByDate = useMemo(() => {
    const map = new Map();

    slots.forEach((slot) => {
      const dateKey = getSlotDateKey(slot);
      if (!dateKey) return;

      const current = map.get(dateKey) || [];
      current.push(slot);
      map.set(
        dateKey,
        current.sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
      );
    });

    return map;
  }, [slots]);

  const selectedDateSlots = useMemo(
    () => (selectedDateKey ? slotsByDate.get(selectedDateKey) || [] : []),
    [selectedDateKey, slotsByDate]
  );

  const calendarDays = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: name === 'duration_minutes' ? Number(value) : value,
      ...(name === 'student_id' ? { link_id: '' } : {})
    }));
  };

  const goToPreviousMonth = () => {
    setSelectedDateKey('');

    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() - 1);
      next.setDate(1);
      return next;
    });
  };

  const goToNextMonth = () => {
    setSelectedDateKey('');

    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      next.setDate(1);
      return next;
    });
  };

  const handleBook = async (slot) => {
    if (!selectedLink || !values.student_id) return;

    const confirmed = window.confirm(
      `Wil je deze priveles definitief inplannen?\n\n` +
        `${formatDateOnly(getSlotDateKey(slot))} ${getSlotLabel(slot)}\n` +
        `${selectedStudent?.student_name || 'Leerling'} — ${selectedLink.docent_name || 'Docent'}`
    );

    if (!confirmed) return;

    try {
      setSavingStartAt(slot.start_at);

      const payload = {
        student_id: values.student_id,
        docent_id: selectedLink.docent_id,
        subject: selectedLink.subject || null,
        location_id: slot.location_id || selectedLink.location_id || selectedStudent?.location_id || null,
        duration_minutes: Number(values.duration_minutes),
        start_at: slot.start_at,
        notes: values.notes || null
      };

      const res = await axios.post(`${API_BASE}/private-lesson/book`, payload, {
        headers: authHeaders()
      });

      setBookedItems((prev) => [
        {
          private_lesson_id: res.data?.private_lesson_id,
          slot_date: getSlotDateKey(slot),
          start_time: slot.start_time,
          end_time: slot.end_time,
          start_at: slot.start_at,
          end_at: slot.end_at,
          subject: selectedLink.subject,
          docent_name: selectedLink.docent_name,
          student_name: selectedStudent?.student_name,
          status: res.data?.status || 'confirmed'
        },
        ...prev
      ]);

      await fetchSlots();
      alert(res.data?.message || 'Priveles definitief ingepland.');
    } catch (err) {
      console.error('Error booking private lesson as admin', err);
      alert(err?.response?.data?.error || 'Inplannen mislukt.');
    } finally {
      setSavingStartAt('');
    }
  };

  if (isLoadingOptions) {
    return (
      <div className="bg-white p-4">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4">
      <h2 className="text-center">Priveles inplannen</h2>
      <p className="text-center text-muted mb-0">
        Plan als administratie direct een bevestigde priveles voor een leerling en docent.
      </p>
      <hr className="seperator" />

      {students.length === 0 ? (
        <>
          <div className="alert alert-warning">
            Er zijn geen leerlingen gevonden binnen jouw toegang.
          </div>
          <Link to="/private-lessons-home" className="btn btn-secondary w-100 rounded-0">
            Terug
          </Link>
        </>
      ) : (
        <>
          <div className="row mb-3">
            <div className="col-md-4 mb-2">
              <label>Zoek leerling</label>
              <input
                type="text"
                className="form-control rounded-0"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                placeholder="Zoek op naam of locatie"
              />
            </div>

            <div className="col-md-4 mb-2">
              <label>Leerling</label>
              <select
                className="form-select rounded-0"
                name="student_id"
                value={values.student_id}
                onChange={handleChange}
              >
                <option value="">Selecteer leerling</option>
                {filteredStudents.map((student) => (
                  <option key={student.student_id} value={student.student_id}>
                    {getStudentLabel(student)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label>Duur</label>
              <select
                className="form-select rounded-0"
                name="duration_minutes"
                value={values.duration_minutes}
                onChange={handleChange}
              >
                {durations.map((duration) => (
                  <option key={duration} value={duration}>
                    {duration} minuten
                  </option>
                ))}
              </select>
            </div>
          </div>

          {values.student_id && availableLinks.length === 0 && (
            <div className="alert alert-warning">
              Deze leerling heeft nog geen actieve privelesdocent-koppeling.
            </div>
          )}

          <div className="row mb-3">
            <div className="col-md-6 mb-2">
              <label>Gekoppelde docent / vak</label>
              <select
                className="form-select rounded-0"
                name="link_id"
                value={values.link_id}
                onChange={handleChange}
                disabled={!values.student_id || availableLinks.length === 0}
              >
                <option value="">Selecteer docent / vak</option>
                {availableLinks.map((link) => (
                  <option key={link.link_id} value={link.link_id}>
                    {link.docent_name || 'Docent'}{link.subject ? ` — ${link.subject}` : ''}
                    {link.location_name ? ` — ${link.location_name}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-2">
              <label>Interne opmerking / lesnotitie</label>
              <input
                type="text"
                className="form-control rounded-0"
                name="notes"
                value={values.notes}
                onChange={handleChange}
                placeholder="Bijvoorbeeld: extra oefenen met breuken"
              />
            </div>
          </div>

          {!selectedLink ? (
            <div className="alert alert-light border">
              Kies eerst een leerling en gekoppelde docent/vak om beschikbare momenten te bekijken.
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPreviousMonth}
                >
                  Vorige
                </button>

                <h4 className="mb-0 text-capitalize">{getMonthLabel(currentMonth)}</h4>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNextMonth}
                >
                  Volgende
                </button>
              </div>

              {isLoadingSlots ? (
                <p>Beschikbare momenten laden...</p>
              ) : slots.length === 0 ? (
                <div className="alert alert-warning">
                  Er zijn geen beschikbare momenten voor deze keuze.
                </div>
              ) : (
                <>
                  <div className="table-responsive mb-4">
                    <table className="table table-bordered text-center align-middle private-calendar-table">
                      <thead>
                        <tr>
                          <th>Ma</th>
                          <th>Di</th>
                          <th>Wo</th>
                          <th>Do</th>
                          <th>Vr</th>
                          <th>Za</th>
                          <th>Zo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIndex) => (
                          <tr key={weekIndex}>
                            {calendarDays.slice(weekIndex * 7, weekIndex * 7 + 7).map((day, dayIndex) => {
                              if (!day) return <td key={`empty-${dayIndex}`} />;

                              const dateKey = toDateKey(day);
                              const hasSlots = slotsByDate.has(dateKey);
                              const isSelected = selectedDateKey === dateKey;

                              return (
                                <td key={dateKey} style={{ height: 72 }}>
                                  <button
                                    type="button"
                                    className={[
                                      'btn',
                                      'btn-sm',
                                      'w-100',
                                      hasSlots ? 'btn-success' : 'btn-outline-light',
                                      isSelected ? 'border border-dark' : ''
                                    ].join(' ')}
                                    disabled={!hasSlots}
                                    onClick={() => setSelectedDateKey(dateKey)}
                                  >
                                    <div>{day.getDate()}</div>
                                    {hasSlots && (
                                      <small>
                                        {slotsByDate.get(dateKey).length} starttijd
                                        {slotsByDate.get(dateKey).length === 1 ? '' : 'en'}
                                      </small>
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h4 className="mb-3">
                    {selectedDateKey
                      ? `Beschikbare tijden op ${formatDateOnly(selectedDateKey)}`
                      : 'Beschikbare tijden'}
                  </h4>

                  {!selectedDateKey ? (
                    <div className="alert alert-light border">
                      Klik op een groene dag om beschikbare tijden te bekijken.
                    </div>
                  ) : selectedDateSlots.length === 0 ? (
                    <div className="alert alert-warning">Geen beschikbare tijden op deze dag.</div>
                  ) : (
                    <div className="private-slot-list">
                      {selectedDateSlots.map((slot) => (
                        <div key={slot.start_at} className="private-slot-card">
                          <div>
                            <div className="private-slot-title">{getSlotLabel(slot)}</div>
                            <div className="private-slot-subtitle">
                              {selectedStudent?.student_name || 'Leerling'} — {selectedLink.docent_name || 'Docent'}
                              {selectedLink.subject ? ` • ${selectedLink.subject}` : ''}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={savingStartAt === slot.start_at}
                            onClick={() => handleBook(slot)}
                          >
                            {savingStartAt === slot.start_at ? 'Bezig...' : 'Definitief inplannen'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {bookedItems.length > 0 && (
            <div className="alert alert-success mt-4">
              <strong>Zojuist definitief ingepland:</strong>
              <ul className="mb-0 mt-2">
                {bookedItems.map((item) => (
                  <li key={`${item.private_lesson_id}-${item.start_at}`}>
                    {formatDateOnly(item.slot_date || item.start_at)} {item.start_time || formatTime(item.start_at)}
                    {' - '}
                    {item.end_time || formatTime(item.end_at)} — {item.student_name || 'Leerling'} — {item.docent_name || 'Docent'} — status: {item.status}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="row mt-4">
            <div className="col-md-6 mb-2">
              <Link to="/private-lessons" className="btn btn-secondary w-100 rounded-0">
                Terug naar privelessen
              </Link>
            </div>
            <div className="col-md-6 mb-2">
              <button
                type="button"
                className="btn btn-outline-secondary w-100 rounded-0"
                onClick={() => navigate('/private-lessons')}
              >
                Klaar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPrivateLessonBooking;
